import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import Profilecard from '../Profilecard';
import profileImage from "../../images/profileImage.png";
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  Users, 
  UserPlus, 
  ChevronDown,
  Loader2,
  RefreshCw,
  UserCheck,
  Star, 
  Award,
  Plus,
  UserX
} from "lucide-react";

export const Following = () => {
  const [members, setMembers] = useState([]);
  const [displayedMembers, setDisplayedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cookie, setCookie] = useCookies(['access_token']);
  const profile = useSelector((state) => state.profile);
  const portalInfo = useSelector((state) => state.portalInfo.data);
  const portalType = portalInfo?.portalType || 'Community';
  const [loading, setLoading] = useState(true);
  const [noUsersFound, setNoUsersFound] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const LIMIT = 6;
  const [totalFollowing, setTotalFollowing] = useState(0);
  const activePage = useRef(1);
  const { id } = useParams();

  const fetchMembers = async (page) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/alumni/${id}/following?page=${page}&size=${LIMIT}`);
      if (response.ok) {
        const data = await response.json();
        setTotalFollowing(data.totalFollowing);
        setMembers(prevMembers => [...prevMembers, ...data.followingDetails]);
      } else {
        console.error("Failed to fetch members");
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (profileLevel) => {
    const badges = {
      0: { label: "SUPER ADMIN", color: "bg-red-600 text-white", icon: <Star size={14} /> },
      1: { label: "ADMIN", color: "bg-orange-600 text-white", icon: <Award size={14} /> },
      2: { label: "ALUMNI", color: "bg-[#0A3A4C] text-white", icon: <Users size={14} /> },
      3: { label: "STUDENT", color: "dynamic-site-bg-3 text-white", icon: <Users size={14} /> },
      4: { label: "MEMBER", color: "dynamic-site-bg-3 text-white", icon: <Users size={14} /> }
    };
    let badge = badges[4];
    
    if(portalType === 'Community'){
      if([0,1].includes(profileLevel)) badge = badges[profileLevel];
    } else {
      badge = badges[profileLevel] || badges[2];
    }

    return (
      <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md ${badge.color} font-medium text-xs`}>
        {badge.icon}
        {badge.label}
      </div>
    );
  };

  const ListViewItem = ({ member }) => {
    return (
      <Link
        to={`/home/members/${member._id}`}
        className="block hover:bg-gray-50 transition-colors duration-200"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <img 
                src={member.profilePicture || profileImage} 
                alt={`${member.firstName} ${member.lastName}`}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {member.firstName} {member.lastName}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getRoleBadge(member.profileLevel)}
                  </div>
                </div>
                <div className="text-xs text-gray-500 sm:text-right">
                  {member.graduatingYear && (
                    <p>Class of {member.graduatingYear}</p>
                  )}
                  {member.department && (
                    <p className="truncate sm:max-w-24">{member.department}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  useEffect(() => {
    fetchMembers(activePage.current);
  }, []);

  useEffect(() => {
    let filteredMembers = members;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredMembers = filteredMembers.filter(
        (member) =>
          member.firstName.toLowerCase().includes(query) ||
          member.lastName.toLowerCase().includes(query)
      );
    }

    setNoUsersFound(filteredMembers.length === 0 && !loading);
    setDisplayedMembers(filteredMembers);
  }, [searchQuery, members, loading]);

  const updateFollowing = () => {
    activePage.current++;
    fetchMembers(activePage.current);
  };

  const clearFilters = () => {
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 dynamic-site-bg rounded-lg flex items-center justify-center">
              <UserPlus size={16} className="sm:size-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Following</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {totalFollowing} following • View the profiles you're following and stay connected
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 mb-4">
          {/* Search Bar */}
          <div className="relative mb-3 sm:mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search following by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-[#0A3A4C] focus:border-[#0A3A4C] transition-all duration-200 text-sm"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              {searchQuery && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm"
                >
                  <RefreshCw size={12} />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid size={14} />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List size={14} />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Following Display */}
        <div className="space-y-4">
          {/* Loading State */}
          {loading && members.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <Loader2 size={24} className="animate-spin text-[#0A3A4C] mr-3" />
              <span className="text-gray-600">Loading following...</span>
            </div>
          )}

          {/* No Results Message */}
          {noUsersFound && !loading && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-6 sm:p-8 text-center">
              <UserX size={40} className="sm:size-48 mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No following found</h3>
              <p className="text-gray-600 text-sm">
                {searchQuery ? 'Try adjusting your search criteria.' : 'You are not following anyone yet.'}
              </p>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && !noUsersFound && !loading && displayedMembers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
              {displayedMembers.map((member) => (
                <Profilecard
                  key={member._id}
                  member={member}
                  name='follow'
                />
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && !noUsersFound && !loading && displayedMembers.length > 0 && (
            <div className="space-y-2">
              {displayedMembers.map((member) => (
                <ListViewItem
                  key={member._id}
                  member={member}
                />
              ))}
            </div>
          )}

          {/* Load More Loading */}
          {loading && members.length > 0 && (
            <div className="flex justify-center items-center py-6">
              <Loader2 size={20} className="animate-spin text-[#0A3A4C] mr-2" />
              <span className="text-gray-600 text-sm">Loading more following...</span>
            </div>
          )}

          {/* Load More Button */}
          {activePage.current < Math.ceil(totalFollowing / LIMIT) && !noUsersFound && !loading && (
            <div className="flex justify-center pt-4">
              <button
                onClick={updateFollowing}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 dynamic-site-bg text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0A3A4C] focus:ring-offset-2 transition-all duration-200 text-sm w-full sm:w-auto"
              >
                <Plus size={16} />
                <span>Load More Following</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Following;
